import React from 'react';
import { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import HttpService from '../../hooks/Https-services';
import { API_ROUTES, hasRole, ROLE_IDS } from '../../utils/constant';
import LoadingButton from '@mui/lab/LoadingButton';
import useCommonDetails from '../../hooks/useCommonDetails';
import { REPORT_OPTIONS, getReportLabel } from './reportOptions';
import { buildReportUrl } from './reportApi';
import { downloadReportByType } from './reportDownload';
import RealTimeReportPage from './realTimeReportPage';
import DownloadIcon from '@mui/icons-material/Download';

type ReportFormValues = {
  reportType: number;
  dateRange: [Date, Date] | null;
};

const ReportPage: React.FC = () => {
  const { allowedMaxDays, afterToday, combine } = DateRangePicker;

  const initialValues: ReportFormValues = {
    reportType: 0,
    dateRange: null,
  };
  const currentUserInfo = useCommonDetails();
  const [teams, setTeams] = React.useState<any[]>([]);
  const [selectedTeam, setSelectedTeam] = React.useState<any | null>(null);

  const handleChangeTeams = (event: any, newValue: any) => {
    setSelectedTeam(newValue || null);
  };
  const fetchTeamsList = React.useCallback(async () => {
    try {
      const response = await HttpService.get(API_ROUTES.fatchAllTeam);
      const teamsData = response.data.data;
      const defaultSelectedTeam =
        teamsData.find((team: any) => team.id === 1) || teamsData[0] || null;

      // ensure options are set before assigning selected value
      setTeams(teamsData);
      setSelectedTeam(defaultSelectedTeam);
    } catch (error: any) {
      console.error('Error fetching teams list:', error);
    }
  }, []);

  // Call fetchTeamsList only once on component mount
  React.useEffect(() => {
    if (
      hasRole(currentUserInfo.roleId, [ROLE_IDS.ADMIN, ROLE_IDS.SUPER_ADMIN])
    ) {
      fetchTeamsList();
    }
  }, [fetchTeamsList, currentUserInfo.roleId]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        reportType: Yup.number()
          .moreThan(0, 'Report type is required')
          .required('Report type is required'),
        dateRange: Yup.array()
          .of(Yup.date().typeError('Invalid date'))
          .required('Date range is required')
          .test(
            'range-length',
            'Please select start and end dates',
            (val) =>
              Array.isArray(val) && val.length === 2 && !!val[0] && !!val[1]
          )
          .test('date-order', 'End date must be after start date', (val) => {
            if (!Array.isArray(val) || val.length !== 2) return true;
            const [s, e] = val;
            if (!s || !e) return true;
            return new Date(e).getTime() >= new Date(s).getTime();
          }),
      }),
    []
  );

  const formik = useFormik<ReportFormValues>({
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!values.dateRange) return;
        const [startDate, endDate] = values.dateRange;

        const payload: any = {
          type: values.reportType,
          startDate: new Date(startDate).toISOString(),
          endDate: new Date(endDate).toISOString(),
        };
        if (selectedTeam?.id) {
          payload.teamIds = String(selectedTeam.id);
        }
        const url = buildReportUrl(payload);
        const res = await HttpService.get(url);

        const data = res?.data;
        const items = Array.isArray(data) ? data : (data?.data ?? []);
        const base = `report_${values.reportType}_${getReportLabel(values.reportType).replace(/\s+/g, '_').toLowerCase()}`;
        downloadReportByType(values.reportType, items, base);
      } catch (e) {
        console.error(e);
        alert('Failed to download report. Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const dateError =
    formik.touched.dateRange && Boolean(formik.errors.dateRange);

  return (
    <>
      {/* <Container maxWidth="md" sx={{ py: 4 }}> */}
      <Typography variant="h5" gutterBottom>
        Report Download
      </Typography>

      <Box component="form" onSubmit={formik.handleSubmit} noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              error={
                formik.touched.reportType && Boolean(formik.errors.reportType)
              }
            >
              <InputLabel id="reportType-label">Report Type</InputLabel>
              <Select
                labelId="reportType-label"
                id="reportType"
                name="reportType"
                label="Report Type"
                value={formik.values.reportType}
                onChange={(e) =>
                  formik.setFieldValue('reportType', Number(e.target.value))
                }
                onBlur={formik.handleBlur}
              >
                <MenuItem value={0} disabled>
                  Select type
                </MenuItem>
                {REPORT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.reportType && formik.errors.reportType && (
                <FormHelperText>{formik.errors.reportType}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <Autocomplete
                size="medium"
                // fullWidth
                id="role-selector"
                options={teams}
                value={selectedTeam}
                disableClearable={Boolean(selectedTeam)}
                getOptionLabel={(option: any) =>
                  typeof option === 'string'
                    ? option
                    : (option?.userTeamKey ?? '')
                }
                onChange={(event, newValue) => {
                  handleChangeTeams(event, newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Teams" />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Date Range
            </Typography>
            <FormControl
              fullWidth
              error={dateError}
              sx={{
                '& .rs-picker-toggle': {
                  borderColor: dateError ? 'error.main' : undefined,
                },
              }}
            >
              <DateRangePicker
                character=" – "
                size="lg"
                shouldDisableDate={combine(allowedMaxDays(31), afterToday())}
                onOpen={() => formik.setFieldTouched('dateRange', true)}
                onChange={(val) => formik.setFieldValue('dateRange', val)}
                value={formik.values.dateRange as any}
                showMeridiem
                format="MM/dd/yyyy hh:mm aa"
              />
              {dateError && (
                <FormHelperText>
                  {formik.errors.dateRange as unknown as string}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <LoadingButton
                type="submit"
                variant="contained"
                loading={formik.isSubmitting}
                startIcon={<DownloadIcon />}
              >
                Download CSV
              </LoadingButton>
              <Button
                type="button"
                variant="outlined"
                onClick={() => formik.resetForm()}
                disabled={formik.isSubmitting}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {/* </Container> */}
      <RealTimeReportPage />
    </>
  );
};

export default ReportPage;
